import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Trash2,
  Loader2,
  AlertTriangle,
  Users,
  FileText,
  Layers,
  Eraser,
  Zap,
  RefreshCw,
} from 'lucide-react';
import api from '../../lib/api';

export function DangerZone({ stats, onCleanupComplete }) {
  const [cleaningOrphaned, setCleaningOrphaned] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleteType, setDeleteType] = useState('');
  const [purging, setPurging] = useState(false);

  const handleCleanOrphanedData = async () => {
    setCleaningOrphaned(true);
    try {
      const response = await api.post('/admin/clean-orphaned-data');
      const details = response.data?.details || {};
      const total = Object.values(details).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0);
      if (total > 0) {
        const parts = [];
        if (details.audit_logs_cleaned) parts.push(`${details.audit_logs_cleaned} audit logs`);
        if (details.supplier_mappings_cleaned) parts.push(`${details.supplier_mappings_cleaned} supplier mappings`);
        if (details.transformations_cleaned) parts.push(`${details.transformations_cleaned} transformations`);
        if (details.orphaned_dispatches_cleaned) parts.push(`${details.orphaned_dispatches_cleaned} orphaned dispatches`);
        if (details.label_reprints_cleaned) parts.push(`${details.label_reprints_cleaned} label reprints`);
        toast.success(`Cleaned: ${parts.join(', ')}`);
      } else {
        toast.info('No orphaned data found');
      }
      onCleanupComplete?.();
    } catch (error) {
      toast.error('Cleanup failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCleaningOrphaned(false);
    }
  };

  const handleOneClickPurge = async () => {
    setPurging(true);
    try {
      const response = await api.post('/admin/full-system-purge');
      const details = response.data?.details || {};
      const total = Object.values(details).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0);
      if (total > 0) {
        toast.success(`Purged ${total} records: ${response.data?.message || ''}`);
      } else {
        toast.info('No data to purge');
      }
      setConfirmDialogOpen(false);
      setConfirmText('');
      onCleanupComplete?.();
    } catch (error) {
      toast.error('Purge failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPurging(false);
    }
  };

  const handlePurgeBatches = async () => {
    setPurging(true);
    try {
      const response = await api.delete('/admin/purge-batches');
      toast.success(`Purged ${response.data.deleted || 0} batches`);
      setConfirmDialogOpen(false);
      setConfirmText('');
      onCleanupComplete?.();
    } catch (error) {
      toast.error('Purge failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPurging(false);
    }
  };

  const handleFullDataPurge = async () => {
    setPurging(true);
    try {
      const response = await api.post('/admin/full-data-purge');
      toast.success('Full data purge complete');
      setConfirmDialogOpen(false);
      setConfirmText('');
      onCleanupComplete?.();
    } catch (error) {
      toast.error('Purge failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPurging(false);
    }
  };

  const handleClearAllTransactional = async () => {
    setPurging(true);
    try {
      await api.post('/admin/clear-all-transactional');
      toast.success('All transactional data cleared');
      setConfirmDialogOpen(false);
      setConfirmText('');
      onCleanupComplete?.();
    } catch (error) {
      toast.error('Failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPurging(false);
    }
  };

  const getConfirmWord = () => {
    switch (deleteType) {
      case 'all-transactional': return 'DELETE ALL';
      case 'purge-batches': return 'PURGE';
      case 'purge-all-traces': return 'PURGE ALL';
      case 'one-click-purge': return 'PURGE';
      default: return 'DELETE';
    }
  };

  const getConfirmHandler = () => {
    switch (deleteType) {
      case 'all-transactional': return handleClearAllTransactional;
      case 'purge-batches': return handlePurgeBatches;
      case 'purge-all-traces': return handleFullDataPurge;
      case 'one-click-purge': return handleOneClickPurge;
      default: return () => {};
    }
  };

  return (
    <>
      <Card className="border-red-200 bg-red-50/50" data-testid="danger-zone">
        <CardHeader>
          <CardTitle className="text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Danger Zone - Data Cleanup
          </CardTitle>
          <CardDescription className="text-red-600">
            These operations help clean up test data. Use with caution.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Quick Cleanup */}
          <div className="p-4 border border-amber-200 rounded-lg bg-amber-50 space-y-3">
            <div className="flex items-center gap-2">
              <Eraser className="w-5 h-5 text-amber-600" />
              <h4 className="font-medium text-amber-800">Quick Cleanup (Safe)</h4>
            </div>
            <p className="text-sm text-muted-foreground">
              Removes orphaned data: old audit logs (90+ days), invalid supplier mappings,
              orphaned transformations, and deleted dispatch remnants. Preserves all active data.
            </p>
            <Button
              variant="outline"
              className="border-amber-400 text-amber-700 hover:bg-amber-100"
              onClick={handleCleanOrphanedData}
              disabled={cleaningOrphaned}
              data-testid="btn-clean-orphaned"
            >
              {cleaningOrphaned ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cleaning...</>
              ) : (
                <><Eraser className="w-4 h-4 mr-2" /> Clean Orphaned Data</>
              )}
            </Button>
          </div>

          {/* Full Purge */}
          <div className="p-4 border border-red-200 rounded-lg bg-white space-y-3">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-red-600" />
              <h4 className="font-medium text-red-700">Full System Purge</h4>
            </div>
            <p className="text-sm text-muted-foreground">
              <strong>One-click</strong> delete ALL: Dispatches, Batches, Transformations,
              Supplier Mappings, Audit Logs. <br/>
              <span className="text-green-700 font-medium">Preserves: Customers, Products, Co-brandings, Settings</span>
            </p>
            <Button
              variant="destructive"
              onClick={() => { setDeleteType('one-click-purge'); setConfirmText(''); setConfirmDialogOpen(true); }}
              data-testid="btn-one-click-purge"
            >
              <Zap className="w-4 h-4 mr-2" /> One-Click Purge All Transactional Data
            </Button>
          </div>

          {/* Selective Purge */}
          <div className="p-4 border border-red-200 rounded-lg bg-white space-y-3">
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-red-600" />
              <h4 className="font-medium text-red-700">Selective Purge</h4>
            </div>
            <p className="text-sm text-muted-foreground">Delete specific data types individually.</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" className="border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => { setDeleteType('purge-batches'); setConfirmText(''); setConfirmDialogOpen(true); }}
                data-testid="btn-purge-batches"
              >
                <Layers className="w-4 h-4 mr-2" /> Batches ({stats.totalBatches || 0})
              </Button>
              <Button variant="outline" size="sm" className="border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => { setDeleteType('purge-all-traces'); setConfirmText(''); setConfirmDialogOpen(true); }}
                data-testid="btn-purge-traces"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> All Traces (Batches + Dispatches + Logs)
              </Button>
            </div>
          </div>

          {/* Nuclear Option */}
          <div className="p-4 border-2 border-red-400 rounded-lg bg-red-100 space-y-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-700" />
              <h4 className="font-medium text-red-700">Complete Data Reset</h4>
            </div>
            <p className="text-sm text-red-600">Delete EVERYTHING including Customers. Only use to start completely fresh.</p>
            <Button variant="destructive"
              onClick={() => { setDeleteType('all-transactional'); setConfirmText(''); setConfirmDialogOpen(true); }}
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete ALL Data (Including Customers)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {deleteType === 'purge-batches' || deleteType === 'purge-all-traces' || deleteType === 'one-click-purge'
                ? 'Confirm Purge'
                : 'Confirm Deletion'}
            </DialogTitle>
            <DialogDescription>
              {deleteType === 'all-transactional'
                ? `This will permanently delete ALL ${stats.totalCustomers} customers, ${stats.totalDispatches} dispatches, and ${stats.totalBatches} batches.`
                : deleteType === 'purge-batches'
                  ? `This will permanently HARD DELETE all ${stats.totalBatches} batches from the batch register.`
                  : deleteType === 'purge-all-traces'
                    ? `This will permanently delete: ${stats.totalDispatches} dispatches, ${stats.totalBatches} batches, all supplier product code mappings, transformations, and audit logs.`
                    : `This will permanently delete ALL transactional data: Dispatches, Batches, Transformations, Supplier Mappings, and Audit Logs.`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Type "{getConfirmWord()}" to confirm:</Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={getConfirmWord()}
              className="mt-2 font-mono"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)} disabled={purging}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={getConfirmHandler()}
              disabled={purging || confirmText !== getConfirmWord()}
            >
              {purging ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {deleteType.includes('purge') || deleteType === 'one-click-purge' ? 'Purging...' : 'Deleting...'}</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> {deleteType.includes('purge') || deleteType === 'one-click-purge' ? 'Confirm Purge' : 'Confirm Delete'}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function EntityBrowser({
  customers, dispatches, batches,
  selectedCustomers, setSelectedCustomers,
  selectedDispatches, setSelectedDispatches,
  selectedBatches, setSelectedBatches,
  filters, setFilters,
  stats, onDelete
}) {
  const filteredCustomers = React.useMemo(() => {
    if (filters.customerFilter === 'no-address') return customers.filter(c => !c.delivery_address_line1);
    if (filters.customerFilter === 'no-orders') return customers.filter(c => !c.has_orders);
    return customers;
  }, [customers, filters.customerFilter]);

  const filteredDispatches = React.useMemo(() => {
    if (filters.dispatchFilter === 'draft') return dispatches.filter(d => d.status === 'draft');
    if (filters.dispatchFilter === 'orphaned') return dispatches.filter(d => !d.customer_name);
    return dispatches;
  }, [dispatches, filters.dispatchFilter]);

  return (
    <div className="space-y-6" data-testid="entity-browser">
      {/* Customers */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> Customers ({filteredCustomers.length})</CardTitle>
              <CardDescription>Select and delete customers in bulk</CardDescription>
            </div>
            <Select value={filters.customerFilter} onValueChange={(v) => setFilters({...filters, customerFilter: v})}>
              <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Customers</SelectItem>
                <SelectItem value="no-address">Without Address ({stats.customersNoAddress})</SelectItem>
                <SelectItem value="no-orders">Without Orders ({stats.customersNoOrders})</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-4">
            <Checkbox
              checked={selectedCustomers.length === filteredCustomers.length && filteredCustomers.length > 0}
              onCheckedChange={(checked) => setSelectedCustomers(checked ? filteredCustomers.map(c => c.id) : [])}
            />
            <span className="text-sm">Select all ({filteredCustomers.length})</span>
            {selectedCustomers.length > 0 && (
              <Button variant="destructive" size="sm" onClick={() => onDelete('customers')}>
                <Trash2 className="w-4 h-4 mr-2" /> Delete Selected ({selectedCustomers.length})
              </Button>
            )}
          </div>
          <div className="max-h-60 overflow-y-auto space-y-2 border rounded-lg p-2">
            {filteredCustomers.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No customers matching filter</p>
            ) : filteredCustomers.map((customer) => (
              <div key={customer.id} className="flex items-center gap-3 p-2 hover:bg-muted/50 rounded">
                <Checkbox
                  checked={selectedCustomers.includes(customer.id)}
                  onCheckedChange={(checked) => {
                    setSelectedCustomers(checked
                      ? [...selectedCustomers, customer.id]
                      : selectedCustomers.filter(id => id !== customer.id));
                  }}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{customer.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {customer.delivery_address_line1 || 'No address'}
                    {customer.delivery_city && `, ${customer.delivery_city}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Dispatches */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> Dispatches ({filteredDispatches.length})</CardTitle>
              <CardDescription>Select and delete dispatches in bulk</CardDescription>
            </div>
            <Select value={filters.dispatchFilter} onValueChange={(v) => setFilters({...filters, dispatchFilter: v})}>
              <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Dispatches</SelectItem>
                <SelectItem value="draft">Drafts Only ({stats.draftDispatches})</SelectItem>
                <SelectItem value="orphaned">Orphaned ({stats.orphanedDispatches})</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-4">
            <Checkbox
              checked={selectedDispatches.length === filteredDispatches.length && filteredDispatches.length > 0}
              onCheckedChange={(checked) => setSelectedDispatches(checked ? filteredDispatches.map(d => d.id) : [])}
            />
            <span className="text-sm">Select all ({filteredDispatches.length})</span>
            {selectedDispatches.length > 0 && (
              <Button variant="destructive" size="sm" onClick={() => onDelete('dispatches')}>
                <Trash2 className="w-4 h-4 mr-2" /> Delete Selected ({selectedDispatches.length})
              </Button>
            )}
          </div>
          <div className="max-h-60 overflow-y-auto space-y-2 border rounded-lg p-2">
            {filteredDispatches.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No dispatches matching filter</p>
            ) : filteredDispatches.map((dispatch) => (
              <div key={dispatch.id} className="flex items-center gap-3 p-2 hover:bg-muted/50 rounded">
                <Checkbox
                  checked={selectedDispatches.includes(dispatch.id)}
                  onCheckedChange={(checked) => {
                    setSelectedDispatches(checked
                      ? [...selectedDispatches, dispatch.id]
                      : selectedDispatches.filter(id => id !== dispatch.id));
                  }}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium">#{dispatch.dispatch_number}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {dispatch.customer_name || 'Unknown customer'} - {dispatch.status}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Batches */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2"><Layers className="w-5 h-5" /> Batches ({batches.length})</CardTitle>
              <CardDescription>Select and permanently delete batches</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-4">
            <Checkbox
              checked={selectedBatches.length === batches.length && batches.length > 0}
              onCheckedChange={(checked) => setSelectedBatches(checked ? batches.map(b => b.id) : [])}
            />
            <span className="text-sm">Select all ({batches.length})</span>
            {selectedBatches.length > 0 && (
              <Button variant="destructive" size="sm" onClick={() => onDelete('batches')} data-testid="delete-selected-batches-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Delete Selected ({selectedBatches.length})
              </Button>
            )}
          </div>
          <div className="max-h-60 overflow-y-auto space-y-2 border rounded-lg p-2">
            {batches.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No batches found</p>
            ) : batches.map((batch) => (
              <div key={batch.id} className="flex items-center gap-3 p-2 hover:bg-muted/50 rounded">
                <Checkbox
                  checked={selectedBatches.includes(batch.id)}
                  onCheckedChange={(checked) => {
                    setSelectedBatches(checked
                      ? [...selectedBatches, batch.id]
                      : selectedBatches.filter(id => id !== batch.id));
                  }}
                  data-testid={`batch-checkbox-${batch.id}`}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium font-mono">{batch.batch_code}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {batch.product_name || 'Unknown product'} - Qty: {batch.quantity_produced || 0}
                  </p>
                </div>
                <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => { setSelectedBatches([batch.id]); onDelete('batches'); }}
                  data-testid={`delete-batch-${batch.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
